import address from "@assets/onedelivery-onboarding/address.svg";
import manage from "@assets/onedelivery-onboarding/manage.svg";
import notify from "@assets/onedelivery-onboarding/notify.svg";
import pay from "@assets/onedelivery-onboarding/pay.svg";
import shop from "@assets/onedelivery-onboarding/shop.svg";
import subscribe from "@assets/onedelivery-onboarding/subscribe.svg";
import welcome from "@assets/onedelivery-onboarding/welcome.svg";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { Button } from "@shadcn/ui/button";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import z from "zod";
import { AppHeader } from "~/components/layout/AppHeader";
import { BackLink } from "~/components/one-delivery/BackLink";
import { PageContainer } from "~/components/layout/PageContainer";
import { ONBOARDING_STEPS } from "~/components/one-delivery/onboarding";

export const Route = createFileRoute("/one-delivery/onboarding/")({
	beforeLoad: () => {
		document.title = "bacehub | One Delivery";
	},
	component: RouteComponent,
	validateSearch: zodValidator(z.object({ step: z.number().int().min(1).max(ONBOARDING_STEPS).optional() })),
});

const ILLUSTRATIONS = [welcome, subscribe, shop, address, notify, manage, pay];

/** Intro steps of the Figma "OD - Order now - Onboarding" frames, followed by the pricing page. */
function RouteComponent() {
	const i18n = useI18nContext().LL["new-ui"];
	const navigate = useNavigate();
	const router = useRouter();
	const { step = 1 } = Route.useSearch();
	const steps = [
		[i18n.OD_ONB_1_TITLE(), i18n.OD_ONB_1_TEXT()],
		[i18n.OD_ONB_2_TITLE(), i18n.OD_ONB_2_TEXT()],
		[i18n.OD_ONB_3_TITLE(), i18n.OD_ONB_3_TEXT()],
		[i18n.OD_ONB_4_TITLE(), i18n.OD_ONB_4_TEXT()],
		[i18n.OD_ONB_5_TITLE(), i18n.OD_ONB_5_TEXT()],
		[i18n.OD_ONB_6_TITLE(), i18n.OD_ONB_6_TEXT()],
		[i18n.OD_ONB_7_TITLE(), i18n.OD_ONB_7_TEXT()],
	] as const;
	const [title, text] = steps[step - 1] ?? steps[0];
	const last = step === ONBOARDING_STEPS;
	const toPricing = () => navigate({ to: "/one-delivery/onboarding/pricing" });
	const back = () => {
		if (step > 1) void navigate({ to: "/one-delivery/onboarding", search: { step: step - 1 }, replace: true });
		else if (router.history.canGoBack()) router.history.back();
		else void navigate({ to: "/" });
	};

	return (
		<PageContainer
			className="min-h-dvh bg-slate-50"
			footer={
				<div className="flex flex-col gap-4">
					<Button
						size="lg"
						className="w-full font-breit font-normal shadow-xl"
						onClick={() => (last ? toPricing() : navigate({ to: "/one-delivery/onboarding", search: { step: step + 1 }, replace: true }))}
					>
						{i18n.OD_ONB_NEXT()}
					</Button>
					{step > 1 && !last && (
						<Button size="lg" variant="ghost" className="w-full font-breit font-normal" onClick={toPricing}>
							{i18n.OD_ONB_SKIP()}
						</Button>
					)}
				</div>
			}
		>
			<AppHeader />
			<BackLink onClick={back} />
			<div className="flex h-[250px] justify-center overflow-hidden px-6 py-4">
				<img src={ILLUSTRATIONS[step - 1]} alt="" className="h-auto max-w-full self-start" />
			</div>
			<div className="flex flex-col gap-1 px-6 pb-8">
				<h1 className="font-breit text-2xl font-normal text-neutral-900">
					<T>{title}</T>
				</h1>
				<p className="whitespace-pre-line text-base text-neutral-500">{text}</p>
			</div>
		</PageContainer>
	);
}
